/**
 * Multi-selection view emitted by `UppKonvaArea.onGrouped$`: alignment, distribution, and uniform geometry.
 */

import type { UppKonvaAxis, UppKonvaShapeKind } from './upp-konva-types';
import { UppKonvaShape } from './upp-konva-shape';

/** Type of each `onGrouped$` emission (in addition to `null`). */
export type UppKonvaGroupedEmission = UppKonvaGroup | null;

/**
 * Multi-selection operations that require the Konva engine (screen coordinates). The library injects a delegate when
 * constructing the `UppKonvaGroup` emitted on `onGrouped$`; if the group is built manually, `align` / `distribute` are no-ops.
 */
export interface UppKonvaGroupStageDelegate {
    /** @param axis - Screen axis for alignment. */
    align(axis: UppKonvaAxis): void;
    /** @param axis - Screen axis for distribution. */
    distribute(axis: UppKonvaAxis): void;
    /**
     * @param widthCm - Uniform width in cm.
     * @param heightCm - Uniform height in cm.
     * @param rotationDeg - Uniform rotation in degrees.
     * @returns `true` when the stage applied the change.
     */
    applyUniformGeometry(widthCm: number, heightCm: number, rotationDeg: number): boolean;
}

/**
 * Multi-selection view (≥2 items). On `onGrouped$` the library supplies instances wired with a stage delegate;
 * `selection` holds the same references as `area.shapes`.
 */
export class UppKonvaGroup {
    // --------------------------------------------------------------------------
    // 1. Lifecycle
    // --------------------------------------------------------------------------

    /**
     * @param _stageDelegate - Stage delegate; the library usually supplies one. `null` for manually constructed groups
     *     (operations are no-ops).
     */
    constructor(private readonly _stageDelegate: UppKonvaGroupStageDelegate | null = null) {}

    // --------------------------------------------------------------------------
    // 2. Properties (integrator)
    // --------------------------------------------------------------------------

    /** Model shapes (`area.shapes`); not copies. */
    get selection(): readonly UppKonvaShape[] {
        return this._selection;
    }

    set selection(next: readonly UppKonvaShape[]) {
        this._selection = [...next];
        // TODO: sync internal stage selection (same references as `area.shapes`).
    }

    /**
     * Common `kind` when every selected shape shares the same type and there are ≥2 items; otherwise `null`.
     *
     * @returns The shared `UppKonvaShapeKind`, or `null`.
     */
    get type(): UppKonvaShapeKind | null {
        const sh = this._selection;
        if (sh.length < 2) {
            return null;
        }
        const k0 = sh[0]?.kind;
        if (!k0) {
            return null;
        }
        return sh.every((s) => s.kind === k0) ? k0 : null;
    }

    get x(): number | null {
        return this._uniformScalar((s) => s.x);
    }

    set x(v: number) {
        if (!this._guardGeom('x') || !Number.isFinite(v)) {
            return;
        }
        const sh = this._selection;
        const first = sh[0];
        if (!first) {
            return;
        }
        const dx = v - first.x;
        if (dx === 0) {
            return;
        }
        for (const s of sh) {
            s.x = s.x + dx;
        }
    }

    get y(): number | null {
        return this._uniformScalar((s) => s.y);
    }

    set y(v: number) {
        if (!this._guardGeom('y') || !Number.isFinite(v)) {
            return;
        }
        const sh = this._selection;
        const first = sh[0];
        if (!first) {
            return;
        }
        const dy = v - first.y;
        if (dy === 0) {
            return;
        }
        for (const s of sh) {
            s.y = s.y + dy;
        }
    }

    get width(): number | null {
        return this._uniformScalar((s) => s.width);
    }

    set width(v: number) {
        if (!this._guardGeom('width') || !Number.isFinite(v)) {
            return;
        }
        for (const s of this._selection) {
            s.width = v;
        }
    }

    get height(): number | null {
        return this._uniformScalar((s) => s.height);
    }

    set height(v: number) {
        if (!this._guardGeom('height') || !Number.isFinite(v)) {
            return;
        }
        for (const s of this._selection) {
            s.height = v;
        }
    }

    get rotation(): number | null {
        return this._uniformScalar((s) => s.rotation);
    }

    set rotation(v: number) {
        if (!this._guardGeom('rotation') || !Number.isFinite(v)) {
            return;
        }
        for (const s of this._selection) {
            s.rotation = v;
        }
    }

    // --------------------------------------------------------------------------
    // 3. Public methods (integrator)
    // --------------------------------------------------------------------------

    /** Aligns centers on the screen axis (same rule as the stage). No-op without a delegate. */
    align(axis: UppKonvaAxis): void {
        this._stageDelegate?.align(axis);
    }

    /** Even spacing along the screen axis. No-op without a delegate. */
    distribute(axis: UppKonvaAxis): void {
        this._stageDelegate?.distribute(axis);
    }

    /**
     * Applies the same width, height, and rotation to every selected transformable of the same `kind`.
     *
     * @param widthCm - Width in cm.
     * @param heightCm - Height in cm.
     * @param rotationDeg - Rotation in degrees.
     * @returns `true` when the stage applied the change; `false` without a delegate.
     */
    applyUniformGeometry(widthCm: number, heightCm: number, rotationDeg: number): boolean {
        return this._stageDelegate?.applyUniformGeometry(widthCm, heightCm, rotationDeg) ?? false;
    }

    /**
     * Removes every shape in `selection` from the area (one structural emission and a single host selection clear when
     * they share the same `parentArea`).
     */
    remove(): void {
        const list = [...this._selection];
        this._selection = [];
        if (list.length === 0) {
            return;
        }
        const first = list[0];
        if (!first) {
            return;
        }
        const area = first.parentArea;
        if (area != null && list.every((s) => s.parentArea === area)) {
            area.removeShapes(list);
        } else {
            for (const s of list) {
                void s.remove();
            }
        }
    }

    // --------------------------------------------------------------------------
    // 4. Private members
    // --------------------------------------------------------------------------

    private _selection: UppKonvaShape[] = [];

    private _uniformScalar(read: (s: UppKonvaShape) => number): number | null {
        if (this.type === null) {
            return null;
        }
        const sh = this._selection;
        if (sh.length === 0) {
            return null;
        }
        const first = sh[0];
        if (!first) {
            return null;
        }
        const v0 = read(first);
        return sh.every((s) => read(s) === v0) ? v0 : null;
    }

    private _guardGeom(prop: string): boolean {
        if (this.type === null) {
            console.error(
                `upp-konva: UppKonvaGroup: cannot set ${prop} when type is null (fewer than two items, or mixed UppKonvaShape.kind).`,
            );
            return false;
        }
        return true;
    }
}
